import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, FormControlStatus, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HeaderForm } from '../../../../form-model/cwo-header.form.model';
import { Shop } from '../../../../models/shop.model';
import { Equipment } from '../../../../models/equipment.model';
import { DamageCause } from '../../../../models/damage-cause.model';
import { Mode } from '../../../../models/mode.model';
import { MasterDataService } from '../../../../services/master-data.service';
import '@maersk-global/mds-components-core/mc-input';
import { catchError, distinctUntilChanged, EMPTY, firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';
import { SharedDataService } from '../../../../services/shared-data.service';
import { MaintenanceOrderResponse } from '../../../../models/maintenance-order-response.model';
import { WorkOrderService } from '../../../../services/work-order.service';
import { PrincipalProperty } from '../../../../models/principal-property.model';
import { APICallStatus } from '../../../../models/api-call-status.mode';
import { ApplicationtTextService } from '../../../../services/application-text.service';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-notification';
@Component({
  selector: 'app-edit-work-order-header',
  imports: [ReactiveFormsModule],
  templateUrl: './edit-work-order-header.component.html',
  styleUrl: './edit-work-order-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EditWorkOrderHeaderComponent implements OnInit, OnDestroy {
  @Input({ required: true }) headerForm!: FormGroup<HeaderForm>;
  @Input() storedExceptionReason: string | null = null;
  workorderId = input.required<string>();
  causes = signal<DamageCause[]>([]);
  workOrder: MaintenanceOrderResponse | undefined;
  equipment: Equipment | null = null;
  selectedMode = signal<Mode>({} as Mode);
  selectedShop = signal<Shop>({} as Shop);
  gensetHours = signal<number | null>(null);
  selectedCause = signal<DamageCause>({} as DamageCause);
  isThirdPartyDamage = signal(false);
  masterDataApiCallStatus = signal<APICallStatus>('NOT_STARTED');
  @Output() headerFormValid = new EventEmitter<FormControlStatus>();
  @Output() modeChanged = new EventEmitter<Mode>();
  @Output() shopChanged = new EventEmitter<Shop>();
  @Output() equipmentChanged = new EventEmitter<Equipment>();
  @Output() principalPropertyChanged = new EventEmitter<PrincipalProperty>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  toastrService: any;

  get displayedExceptionReason(): string | null {
    return this.storedExceptionReason ?? this.workOrder?.modeRestrictionExceptionReason ?? null;
  }

  constructor(
    private formBuilder: FormBuilder,
    private masterDataService: MasterDataService,
    private sharedDataService: SharedDataService,
    private workorderService: WorkOrderService,
    private applicationTextService: ApplicationtTextService,
  ) {}
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  async ngOnInit() {
    this.initForm();
    this.headerForm.controls.cause.valueChanges.subscribe((damageCause) => {
      if (damageCause.code === 'CONFIRMED_3RD_PARTY') {
        this.headerForm.controls.thirdPartyPort.addValidators([Validators.required, Validators.maxLength(10)]);
        this.headerForm.controls.thirdPartyPort.updateValueAndValidity();
        this.isThirdPartyDamage.set(true);
      } else {
        this.isThirdPartyDamage.set(false);
        this.headerForm.controls.thirdPartyPort.clearValidators();
        this.headerForm.controls.thirdPartyPort.updateValueAndValidity();
      }
    });
    this.headerForm.statusChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged()).subscribe((status) => {
      this.headerFormValid.emit(status);
    });
    let savedWorkOrder = await firstValueFrom(this.sharedDataService.getWorkOrder());
    let savedEquipment = await firstValueFrom(this.sharedDataService.getEquipment());
    if (savedWorkOrder) {
      this.workOrder = savedWorkOrder;
    } else {
      this.workOrder = await firstValueFrom(this.workorderService.getWorkOrderById(this.workorderId()));
    }
    let equipmentNumber = this.workOrder?.equipmentNumber ?? '';
    if (savedEquipment) {
      this.equipment = savedEquipment;
    } else {
      this.equipment = await firstValueFrom(this.workorderService.getEquipmentDetails(this.workorderId()));
      if (this.equipment) {
        this.equipment.equipmentNumber = equipmentNumber;
      }
    }

    this.masterDataApiCallStatus.set('IN_PROGRESS');
    await Promise.allSettled([
      this.getAndSetMode(this.workOrder?.maintenanceRepairModeCode ?? ''),
      this.getAndSetShop(this.workOrder?.maintenanceShopCode ?? '', this.workOrder?.maintenanceRepairModeCode ?? ''),
      this.getAndSetCause(),
    ]);
    this.setFormFields();
    this.modeChanged.emit(this.selectedMode());
    this.shopChanged.emit(this.selectedShop());
    if (this.equipment) {
      this.equipmentChanged.emit(this.equipment);
      this.principalPropertyChanged.emit({
        Equipment: this.equipment,
        Mode: this.selectedMode(),
        Shop: this.selectedShop(),
        HeaderFormStatus: this.headerForm.status,
      });
    }
    this.masterDataApiCallStatus.set('COMPLETED_SUCCESFULLY');

    this.getGensetHours();
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  private initForm() {
    this.headerForm.addControl('shopCode', this.formBuilder.nonNullable.control<Shop>({} as Shop));
    this.headerForm.addControl('equipmentNumber', this.formBuilder.nonNullable.control<string>(''));
    this.headerForm.addControl('proxyEquipmentNumberForAsyncValidations', this.formBuilder.control<Equipment | null>(null));
    this.headerForm.addControl('thirdPartyPort', this.formBuilder.nonNullable.control<string>(''));
    this.headerForm.addControl(
      'vendorRefNo',
      this.formBuilder.nonNullable.control<string>('', [Validators.maxLength(20), Validators.pattern(/^[^,&%]+$/)]),
    );
    this.headerForm.addControl('cause', this.formBuilder.nonNullable.control<DamageCause>({} as DamageCause));
    this.headerForm.addControl('mode', this.formBuilder.nonNullable.control<Mode>({} as Mode));
    this.headerForm.addControl('currency', this.formBuilder.nonNullable.control<string>(''));
    this.headerForm.addControl('macOk', this.formBuilder.nonNullable.control<boolean>(false));
    this.headerForm.addControl('boxOk', this.formBuilder.nonNullable.control<boolean>(false));
  }

  private async getAndSetShop(shopCode: string, repairModeCode: string) {
    let result = await Promise.all([
      firstValueFrom(this.masterDataService.getShopsByShopCode(shopCode)),
      firstValueFrom(this.masterDataService.getShopRepairCostLimitByMode(shopCode, repairModeCode)),
    ]);
    let shop = result[0];
    shop.shopRepairCostAllocations = result[1];
    this.selectedShop.set(shop);
  }

  private async getAndSetMode(repairModeCode: string) {
    let mode = await firstValueFrom(this.masterDataService.getRepairMode(repairModeCode));
    this.selectedMode.set(mode);
  }

  private async getAndSetCause() {
    this.causes.set(await firstValueFrom(this.masterDataService.getDamageCauses()));
    let selectedCause = this.causes().filter((cause) => cause.code == this.workOrder?.maintenanceOrderCause)[0];
    this.selectedCause.set(selectedCause);
  }

  private setFormFields() {
    this.headerForm.setValue({
      shopCode: this.selectedShop(),
      equipmentNumber: this.workOrder?.equipmentNumber ?? '',
      proxyEquipmentNumberForAsyncValidations: this.equipment,
      thirdPartyPort: this.workOrder?.damagedAt ?? '',
      vendorRefNo: this.workOrder?.vendorsReference ?? '',
      cause: this.selectedCause(),
      mode: this.selectedMode(),
      currency: this.workOrder?.isoCurrencyCode ?? '',
      macOk: this.workOrder?.reeferContainerCondition === 'MAC_OK' ? true : false,
      boxOk: this.workOrder?.reeferContainerCondition === 'BOX_OK' ? true : false,
    });
  }

  getGensetHours() {
    if (this.workorderId() && this.equipment?.equipmentTypeParentCode === 'GENS') {
      this.workorderService
        .getGensetHours(this.workorderId())
        .pipe(
          takeUntil(this.destroyed$),
          catchError((err) => {
            this.toastrService.showToastr(this.applicationTextService.unableToGetGensetHours, 'error');
            return EMPTY;
          }),
        )
        .subscribe((response) => {
          this.gensetHours.set(response);
        });
    }
  }
}
